window.StockCubeCore = window.StockCubeCore || {};

window.StockCubeCore.renderStockBars = async function (stocks, topN = Infinity) {
    const container = this.el;
    container.innerHTML = "";
    this._billboardEls = null;

    this.originalContainerY = null;
    this.viewportAdjustmentApplied = false;

    const dateElRestore = document.querySelector('#current-time-date');
    if (dateElRestore) {
        const isVR = this.platformConfig && this.platformConfig.platform === 'vr';
        const targetY = isVR ? 0.43 : 0.33;
        const dp = dateElRestore.getAttribute('position') || { x: 0, y: 0, z: 0 };
        const dx = typeof dp === 'object' ? dp.x : (parseFloat(String(dp).split(' ')[0]) || 0);
        const dz = typeof dp === 'object' ? dp.z : (parseFloat(String(dp).split(' ')[2]) || 0);
        dateElRestore.setAttribute('position', `${dx} ${targetY} ${dz}`);
    }

    const sectorIds = this.data.sectorIds.map(Number).sort((a, b) => a - b);
    const minColumns = 1;
    const maxColumns = 11;
    const numColumns = Math.max(minColumns, Math.min(sectorIds.length, maxColumns));
    this.numColumns = numColumns;
    const usableHeight = this.usableHeight;
    const targetHeight = usableHeight * 0.9;

    const spacing = this.spacing !== undefined ? this.spacing : 0.05;
    const { barWidth, barDepth } = this.resolveBarGeometry(this.el, this.containerHeight, numColumns, spacing);
    this.barDepth = barDepth;

    let filteredStocks = stocks;
    if (this.isHasSectors) {
        filteredStocks = stocks.filter(stock => sectorIds.includes(stock.sector_id));
    }

    filteredStocks = this.applySipFiltersToStocks(filteredStocks);

    let sortOrderFn = stock => stock.ranking || 0;
    const dsConfig = this.resolveDataSourceConfig(this.data.dataSource, filteredStocks, this.formatNumber.bind(this));
    let { heightValueFn, labelValueFn, maxHeightValue, sortDirection, colorValueFn } = dsConfig;

    if (['mmap', 'sip', 'dividend', 'sip-sec', 'dividend-sec'].includes(this.data.dataSource)) {
        if (window.Fireworks) {
            window.Fireworks.emergencyStopFireworks();
            clearInterval(this.autoLaunchInterval);
        }
    }

    this.heightValueFn = heightValueFn;
    this.labelValueFn = labelValueFn;
    this.sortOrderFn = sortOrderFn;
    this.sortDirection = sortDirection;

    this.maxHeightValue = maxHeightValue;

    if (this.data.dataSource !== 'mmap') {
        const { max: globalMax, hasNegative } = this.calculateGlobalMaxHeightValue(heightValueFn);

        if (globalMax > 0 && globalMax > maxHeightValue) {
            maxHeightValue = globalMax;
            this.maxHeightValue = globalMax;
        } else {
            if (this.maxHeightValue && this.maxHeightValue > maxHeightValue) {
                maxHeightValue = this.maxHeightValue;
            }
            this.maxHeightValue = maxHeightValue;
        }

        if (hasNegative) {
            this.hasNegativeGlobal = true;
        }
    }

    const currentFrameHasNegative = filteredStocks.some(stock => heightValueFn(stock) < 0);
    let hasNegativeValues = this.hasNegativeGlobal || currentFrameHasNegative;
    if (['top-gainers', 'top-gainers-sec', 'top-losers', 'top-losers-sec'].includes(this.data.dataSource)) {
        hasNegativeValues = false;
    }

    this.zeroPlane = 0;

    const heightScale = targetHeight / (maxHeightValue || 1) / (hasNegativeValues ? 2 : 1);
    this.heightScale = heightScale;

    let sectorColors = new Map();
    let sectorGroups = {};
    let maxRows = 0;

    this.stockBars = new Map();
    if (this.sectorLabels) this.sectorLabels.clear();
    else this.sectorLabels = new Map();

    const sectorLabelStyle = this.platformConfig ? this.platformConfig.sectorLabelStyle : {};

    if (this.isHasSectors) {
        const sectorIdToColIndex = new Map();
        sectorIds.forEach((sectorId, index) => {
            sectorIdToColIndex.set(sectorId, index);
            const hue = Math.round((index * 360 / numColumns) % 360);
            sectorColors.set(sectorId, `hsl(${hue}, 80%, 50%)`);
            sectorGroups[sectorId] = [];
        });

        filteredStocks.forEach(stock => {
            if (stock.sector_id != null && sectorIds.includes(stock.sector_id)) {
                sectorGroups[stock.sector_id].push(stock);
            }
        });

        if (['sip-sec', 'dividend-sec'].includes(this.data.dataSource)) {
            Object.keys(sectorGroups).forEach(id => {
                const group = sectorGroups[id] || [];
                sectorGroups[id] = this.applySipFiltersToStocks(group);
            });
        }

        Object.keys(sectorGroups).forEach(sectorId => {
            const stocks = sectorGroups[sectorId];
            if (stocks.length === 0) {
                return;
            }
            const sortFn = sortOrderFn || heightValueFn;
            const hasPerf = stocks.some(stock => stock.perf === 'G' || stock.perf === 'L');
            if (hasPerf) {
                const halfTopN = topN;
                const gainers = stocks.filter(stock => stock.perf === 'G');
                const losers = stocks.filter(stock => stock.perf === 'L');
                gainers.sort((a, b) => sortDirection === 'asc' ? sortFn(a) - sortFn(b) : sortFn(b) - sortFn(a));
                losers.sort((a, b) => sortDirection === 'asc' ? sortFn(a) - sortFn(b) : sortFn(b) - sortFn(a));
                sectorGroups[sectorId] = [...gainers.slice(0, halfTopN), ...losers.slice(0, halfTopN)];
            } else {
                stocks.sort((a, b) => sortDirection === 'asc' ? sortFn(a) - sortFn(b) : sortFn(b) - sortFn(a));
                sectorGroups[sectorId] = stocks.slice(0, topN);
            }
        });

        maxRows = Math.min(topN, Math.max(...Object.values(sectorGroups).map(group => group.length)));

        let globalMinHeight = 0;
        let hasNegativeBars = false;
        sectorIds.forEach((sectorId) => {
            const sectorStocks = sectorGroups[sectorId] || [];
            if (sectorStocks.length > 0) {
                const sectorMinHeight = Math.min(...sectorStocks.map(stock => {
                    const heightValue = heightValueFn(stock);
                    return this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);
                }));
                if (sectorMinHeight < 0) {
                    hasNegativeBars = true;
                    globalMinHeight = Math.min(globalMinHeight, sectorMinHeight);
                }
            }
        });

        const labelY = hasNegativeBars
            ? this.zeroPlane + globalMinHeight - 0.05
            : this.zeroPlane - 0.05;

        sectorIds.forEach((sectorId) => {
            const sectorStocks = sectorGroups[sectorId] || [];
            const colIndex = sectorIdToColIndex.get(sectorId);
            const x = colIndex * (barWidth + spacing) - ((numColumns - 1) * (barWidth + spacing)) / 2;

            const avgHeightValue = sectorStocks.length > 0
                ? sectorStocks.reduce((sum, stock) => sum + heightValueFn(stock), 0) / sectorStocks.length
                : 0;
            const avgColorValue = colorValueFn && sectorStocks.length > 0
                ? sectorStocks.reduce((sum, stock) => sum + colorValueFn(stock), 0) / sectorStocks.length
                : avgHeightValue;
            const labelColor = this.colorMode === 'gradient'
                ? this.getGradientColor(avgColorValue)
                : sectorColors.get(sectorId) || '#FFFFFF';

            if (typeof this.createSectorLabel === 'function') {
                this.createSectorLabel(container, sectorId, x, labelY, labelColor, avgHeightValue);
            }

            sectorStocks.forEach((stock, rank) => {
                const heightValue = heightValueFn(stock);
                const height = this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);

                const absHeight = Math.max(0.001, Math.abs(height));
                const isPositive = height >= 0;
                const barBaseY = isPositive ? this.zeroPlane + (absHeight / 2) : this.zeroPlane - (absHeight / 2);

                const { z } = this.calculateZPositions(maxRows, sectorStocks.length, 'sector', 0, rank);
                const colorInput = colorValueFn ? colorValueFn(stock) : heightValue;
                const color = this.colorMode === 'gradient'
                    ? this.getGradientColor(colorInput)
                    : sectorColors.get(sectorId) || '#FFFFFF';
                stock = { ...stock, tickerDetails: this.stockInfo[stock.ticker]?.tickerDetails };

                const { bar, primaryLabel } = this.createStockBar(
                    stock,
                    x,
                    z,
                    height,
                    color,
                    labelValueFn,
                    container,
                    this.zeroPlane,
                    barWidth,
                    barDepth,
                    'true',
                );
                this.stockBars.set(stock.ticker, {
                    bar,
                    primaryLabel,
                    x,
                    z,
                    sectorId,
                    heightValue,
                    y: barBaseY,
                    lastHeight: height,
                    lastLabel: labelValueFn(stock)
                });
            });
        });

        const wrapperSectors = container.parentElement;
        if (wrapperSectors && this.stockBars.size > 0) {
            setTimeout(() => {
                this.adjustChartToFitViewport?.(
                    this.stockBars,
                    heightScale,
                    maxHeightValue,
                    targetHeight,
                    this.zeroPlane,
                    wrapperSectors
                );
                this.updateBillboards?.(true);
                window.StockCubeCore.recalcLabelPositions.call(this);
                this.calculateSectorLabelPosition?.(this.rotationData);
                if (typeof this.updateLabelsGlowState === 'function') {
                    this.updateLabelsGlowState();
                }
            }, 100);
        }
    } else {
        if (this.isMmapMode && this.data.dataSource === 'mmap' && this.mmapViewMode !== '3d') {
            window.MarketMapSharedRendering.renderMarketMap(this, this.currentData || null);
            return;
        }

        const gridSize = 10;
        const maxRowsGrid = 5;
        const totalBars = Math.min(filteredStocks.length, 50);

        let sortFn = sortOrderFn || heightValueFn;
        filteredStocks.sort((a, b) =>
            sortDirection === 'asc' ? sortFn(a) - sortFn(b) : sortFn(b) - sortFn(a)
        );

        filteredStocks = filteredStocks.slice(0, 50);

        filteredStocks.forEach((stock, index) => {
            const heightValue = heightValueFn(stock);
            const height = this.calculateBarHeight(heightValue, heightScale, maxHeightValue, targetHeight);
            const col = index % gridSize;
            const unit = barWidth + spacing;
            const x = unit * (col - gridSize / 2 + 0.5);
            const { z } = this.calculateZPositions(maxRowsGrid, totalBars, 'grid', index, 0, gridSize);
            const color = this.colorMode === 'gradient' || !stock.sector_id
                ? this.getGradientColor(heightValue)
                : sectorColors.get(stock.sector_id) || this.getGradientColor(heightValue);

            const labelText = typeof labelValueFn === 'function'
                ? labelValueFn(stock)
                : `${stock.ticker}\n${(heightValue || 0).toFixed(1)}%`;

            const { bar, primaryLabel } = this.createStockBar(
                stock,
                x,
                z,
                height,
                color,
                labelValueFn,
                container,
                this.zeroPlane,
                barWidth,
                barDepth,
                'true',
            );
            this.stockBars.set(stock.ticker, {
                bar,
                primaryLabel,
                x,
                z,
                heightValue,
                stock,
                lastHeight: height,
                lastLabel: labelText
            });
        });



        const wrapper = container.parentElement;
        if (wrapper && this.stockBars.size > 0) {
            setTimeout(() => {
                this.adjustChartToFitViewport?.(
                    this.stockBars,
                    heightScale,
                    maxHeightValue,
                    targetHeight,
                    this.zeroPlane,
                    wrapper
                );
                this.updateBillboards?.(true);
                window.StockCubeCore.recalcLabelPositions.call(this);
                this.calculateSectorLabelPosition?.(this.rotationData);
                if (typeof this.updateLabelsGlowState === 'function') {
                    this.updateLabelsGlowState();
                }
            }, 100);
        }
    }
};

window.StockCubeCore.renderFinalFrameStockContent = function (finalStocks) {
    if (this.isMmapMode && this.data.dataSource === 'mmap') {
        this.renderAnimationFrame(finalStocks);
    } else if (this.isFirstRender) {
        this.renderAnimationFrame(finalStocks);
    } else if (this.isHasSectors) {
        this.updateBarsSectors(finalStocks, this.data.topN, this.colorMode, this.isFixed);
    } else {
        this.updateBarsNoSectors(finalStocks, this.data.topN, this.isFixed);
    }
};
